<template>
    <span :style="styleObj"></span>
</template>

<script setup lang="ts">
import {
    computed,
    getCurrentInstance,
    nextTick,
    onBeforeUnmount,
    onMounted,
    ref,
    watch,
} from "vue";
import type { CSSProperties } from "vue/types/jsx";

const frames = computed(() =>
    Math.round(framesPerSecond.value * (durationMs.value / 1000))
);

const framesPerSecond = ref(30);
const durationMs = ref(1000);
const dataUrl = ref<string>();
const running = ref(true);
const currentFrame = ref(0);
const lastFrame = ref(-1);
const requestAnimationId = ref<number>();
const styleObj = ref<CSSProperties>({
    display: "inline-block",
    width: undefined,
    height: undefined,
    "background-image": undefined,
    "background-size": "cover",
    "background-repeat": "no-repeat",
    "background-position-y": "0px",
    "background-position-x": undefined,
});

watch(
    () => frames.value,
    () => {
        nextTick(function () {
            reload();
        });
    }
);

function cssUrl() {
    if (typeof dataUrl.value !== "string" || dataUrl.value == "")
        return undefined;
    return "url(" + dataUrl.value + ")";
}

const props = defineProps({
    size: Number,
});

const instance = getCurrentInstance();

function getComputedStyleForEl() {
    return instance?.proxy.$el != null
        ? getComputedStyle(instance.proxy.$el)
        : undefined;
}

const computedStyle = ref<CSSStyleDeclaration | undefined>(
    getComputedStyleForEl()
);

const pixels = computed(() => {
    if (props.size != null) return props.size;
    if (computedStyle.value != null) {
        return Math.max(
            4,
            Math.round(
                parseFloat(
                    computedStyle.value.fontSize.substring(
                        0,
                        computedStyle.value.fontSize.length - 2
                    )
                )
            )
        );
    }
    return 4;
});

function renderSprite() {
    if (instance == null) throw new Error("instance missing");
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (ctx == null) throw new Error("failed to create ctx");
    const style = getComputedStyle(instance.proxy.$el);
    ctx.font = style.font;

    const nPixels = pixels.value;

    canvas.width = nPixels * frames.value;
    canvas.height = nPixels;

    ctx.lineWidth = Math.max(1, Math.round(nPixels * 0.1));

    const radianStep = (2 * Math.PI) / frames.value;
    let r = 0;
    for (let i = 0; i < frames.value; i++) {
        const left = nPixels * i;
        const centerX = left + nPixels / 2;
        const centerY = nPixels / 2;
        ctx.beginPath();
        ctx.arc(
            centerX,
            centerY,
            nPixels / 2 - ctx.lineWidth * 2,
            r,
            r + 0.5 * Math.PI
        );
        ctx.stroke();
        r += radianStep;
    }

    dataUrl.value = canvas.toDataURL("image/png");
    styleObj.value.width = nPixels + "px";
    styleObj.value.height = nPixels + "px";
    styleObj.value["background-image"] = cssUrl();
}
function animationFrameCallback(ms: number) {
    //console.log("FRAME", this, arguments);
    const newFrameNumber = Math.floor(ms / framesPerSecond.value);
    if (newFrameNumber > lastFrame.value) {
        const nCurrentFrame = currentFrame.value % frames.value;

        const offset = 0 - nCurrentFrame * (pixels.value ?? 4);

        styleObj.value["background-position-x"] = offset + "px";

        currentFrame.value = nCurrentFrame + 1;

        lastFrame.value = newFrameNumber;
    }
    if (running.value === true) {
        requestAnimationId.value = requestAnimationFrame(
            animationFrameCallback
        );
    }
}
function start() {
    renderSprite();
    running.value = true;
    requestAnimationId.value = requestAnimationFrame(animationFrameCallback);
}
function stop() {
    running.value = false;
    if (requestAnimationId.value != null)
        cancelAnimationFrame(requestAnimationId.value);
}
function reload() {
    stop();
    computedStyle.value = getComputedStyleForEl();
    nextTick(function () {
        start();
    });
}

onMounted(() => {
    computedStyle.value = getComputedStyleForEl();
    nextTick(function () {
        start();
    });
});
onBeforeUnmount(() => stop());
</script>
